/**
 * 文字の大きさ（#115、docs/44 §2・docs/10 §9.14）：小・中・大。ログイン前の下の帯（PC版・スマホ版）と、アカウントメニュー・個人設定の「文字の大きさ」の1つの部品。
 *   #152（docs/61 ★i）から見た目は iOS のセグメント（components/Segmented.tsx：地 #EFEEE9 の溝・選んだものは白い地＋藍の太字＋影）。
 *   1つ 44×34px、押せる範囲は上下に 5px ずつ広げて 44px（CSS の ::before）。読み上げは aria-pressed／aria-checked（色だけで伝えない）。
 *   押すとすぐ当てて端末の Cookie gb_text に書く。ログイン中なら人（accounts）にも保存する（PUT /account/preferences）＝別の端末でログインしても同じ大きさ
 */
import { TEXT_SIZES, TEXT_SIZE_LABEL, VIEW_MSG, type AccountPreferences, type Me, type TextSize as Size } from "@genbaryoku/contract";
import { useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { api } from "../api.js";
import { ME_KEY } from "../auth.js";
import { applyTextSize, currentTextSize, subscribeTextSize } from "../lib/view.js";
import { Segmented } from "./Segmented.jsx";

/** いまの文字の大きさ（<html data-text>） */
export function useTextSize(): Size {
  return useSyncExternalStore(subscribeTextSize, currentTextSize, currentTextSize);
}

/** 文字の大きさを変える。ログイン前は端末の Cookie だけ、ログイン中は人にも保存する */
export function useSetTextSize() {
  const qc = useQueryClient();
  return (size: Size) => {
    applyTextSize(size);
    const me = qc.getQueryData<Me | null>(ME_KEY);
    if (!me) return;
    qc.setQueryData<Me>(ME_KEY, { ...me, preferences: { ...me.preferences, textSize: size } });
    api<AccountPreferences>("PUT", "/account/preferences", { textSize: size }).catch((e: unknown) => console.warn("文字サイズを保存できませんでした", e));
  };
}

/**
 * 小・中・大のセグメント。menu＝アカウントメニューの中（role="menuitemradio"。↓↑ で移れる）。
 * labelledBy＝見える「文字の大きさ」の文の id（無ければ読み上げの名前を付ける）
 */
export function TextSize({ menu = false, labelledBy }: { menu?: boolean; labelledBy?: string }) {
  const size = useTextSize();
  const set = useSetTextSize();
  return (
    <Segmented
      className="tsize"
      menu={menu}
      labelledBy={labelledBy}
      label={VIEW_MSG.textSize}
      testId="text-size"
      value={size}
      onChange={set}
      options={TEXT_SIZES.map((k) => ({ value: k, label: TEXT_SIZE_LABEL[k], testId: `text-size-${k}` }))}
    />
  );
}
